import { createContext, useContext, useState, useCallback, FC, ReactNode } from 'react'
import { RenewalData } from '../dtos/renewalData'
import { api } from '../services/apiClient'
import { calculatePercentages } from 'src/utils/applicationStepCalculator'
import { renewalApplicationNavigationRoutes, renewalJudgeApplicationNavigationRoutes } from 'src/constants/application'
import { callToast } from 'src/hooks/useToast'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify';
import { ValidationError, useErrors } from './errors'
import { FirmDetailsContactData } from 'src/dtos/portal'
import ApplicationData from 'src/dtos/applicationData'
import { appendErrorSource } from 'src/utils/serverError'

interface RenewalContextData {
  renewalData: RenewalData;
  updateRenewalData(renewData: RenewalData): void;
  saveRenewalData(renewData: RenewalData): Promise<string>;
  saveRenewalDataForLater(renewData: RenewalData): Promise<void>;
  getRenewalData(): Promise<string>;
}

const RenewalContext = createContext<RenewalContextData>({} as RenewalContextData)

export const RenewalProvider: FC<{ children: ReactNode }> = ({children}) => {
  const [renewalData, setRenewalData] = useState({} as RenewalData)
  const { triggerToast, clearToast } = useErrors()

  const getPrimaryContactInfo = async (appData: RenewalData) => {
    const firmId = appData.firmId
    const contactData: FirmDetailsContactData
    = await (await api.get(`Firm/GetApplicantUserPseudoContact?firmId=${firmId}`)).data

    return contactData
  }

  const prepareApplicationToSave = (appData: RenewalData): RenewalData => {
    const routes = appData?.insuranceType === 'LAWYER' ? renewalApplicationNavigationRoutes : renewalJudgeApplicationNavigationRoutes
    const {
      completionPercentage,
      subSectionPercentage
    } = calculatePercentages(appData.stoppedAt, routes, true, appData?.insuranceType === 'JUDGE')
    
    return {
        ...appData,
        completionPercentage: completionPercentage,
        subSectionPercentage: String(subSectionPercentage)
      }
  }

  const updateRenewalData = useCallback((appData: RenewalData) => {
    setRenewalData(appData)
  }, [setRenewalData])

  const saveRenewalData = useCallback(async (appData: RenewalData) : Promise<string> => {
    clearToast()
    try {
      const primaryContactInfo = await getPrimaryContactInfo(appData)
            
      const { applicant, ...rest } = prepareApplicationToSave(appData)
      const updatedFirmSection = {...rest.firmSection, primaryContact: {
        firstName: primaryContactInfo.firstName,
        lastName: primaryContactInfo.lastName,
        email: primaryContactInfo.emailAddress,
      }}
      
      
      const updatedData = {...rest, firmSection: updatedFirmSection}
      
      var result = await api.patch('/Renewals', {
        step: 1,
        data: {
          ...updatedData
        }
      })
      updateRenewalData(appData)
      return result.data;
    } catch(error: any | AxiosError) {
      // Log actual error details to browser console
      if(error?.response?.data?.errorDetails) {
        console.error(error.response.data.errorDetails)
      }

      // Duplicates application.tsx
      if (error?.response?.status === 400) {
        const data = error?.response?.data
        if (Array.isArray(data)) {
          // FluentValidation errors -> validation toast
          triggerToast(data as ValidationError[])
        } else if (data?.errorMessage) {
          // DomainException (ErrorResponse) -> surface the message
          const isAlreadyComplete = data.errorMessage.includes('already been completed')
          callToast({
            header: isAlreadyComplete ? 'Renewal Complete' : 'Unable to Save',
            footer: appendErrorSource(data.errorMessage.toString(), data),
            type: 'error'
          })
        }
      }
      throw new Error('Error trying to save data. Please, try again')
    }
  }, [updateRenewalData, triggerToast, clearToast])

  const saveRenewalDataForLater = useCallback(async (appData: RenewalData) => {
    clearToast()
    try {
      const { applicant, ...rest } = prepareApplicationToSave(appData)
      await api.post('/Renewals/SaveForLater', {
        step: 1,
        data: {
          ...rest
        }
      })
      updateRenewalData(appData)
    } catch(error: any | AxiosError) {
      // Log actual error details to browser console
      if(error?.response?.data?.errorDetails) {
        console.error(error.response.data.errorDetails)
      }

      // Duplicates application.tsx
      if (error?.response?.status === 400) {
        const data = error?.response?.data
        if (Array.isArray(data)) {
          // FluentValidation errors -> validation toast
          triggerToast(data as ValidationError[])
        } else if (data?.errorMessage) {
          // DomainException (ErrorResponse) -> surface the message
          const isAlreadyComplete = data.errorMessage.includes('already been completed')
          callToast({
            header: isAlreadyComplete ? 'Renewal Complete' : 'Unable to Save',
            footer: appendErrorSource(data.errorMessage.toString(), data),
            type: 'error'
          })
        }
      }
      throw new Error('Error trying to save and send magic link. Please, try again')
    }
  }, [updateRenewalData, triggerToast, clearToast])

  const getRenewalData = useCallback(async () => {
    try {
      const { data } = await api.get<{ id: number, step: string, data: RenewalData, firmId: number }>('/Renewals') 
      setRenewalData(prev => ({ ...prev, ...data.data, id: data.id, firmId: data.firmId }))

      if(data.data.aopRedirectRoute != null){
        return data.data.aopRedirectRoute;
      }

      if (!!data.data.stoppedAt) {
        return data.data.stoppedAt;
      }

      // the processedAttorneys and setRenewalData on next lines are to force empty field
      // for hoursOnBill, client wants to force get this info updated every renewal
      const processedAttorneys = data.data.attorneysSection?.attorneys?.map(att => ({
        ...att,
        hoursOnBill: undefined
      }));
  
      setRenewalData(prev => ({
        ...prev,
        ...data.data,
        id: data.id,
        firmId: data.firmId,
        attorneysSection: {
          ...data.data.attorneysSection,
          attorneys: processedAttorneys
        }
      }));

      return '/customer/renew/begin';

    } catch(error : any) {
      const data = error?.response?.data
      let errorMessage = data?.errorMessage ?? 'Please try again later'
      const errorDetails = data?.errorDetails

      if(errorMessage.includes('401')){
        errorMessage = 'unexpected authorization error, please contact administrator'
      }

      // Log actual error details to browser console
      if(errorDetails) {
        console.error(errorDetails)
      }

      callToast({
        header: 'Unexpected Error',
        footer: appendErrorSource(errorMessage.toString(), data),
        type: 'error'
      })
      return '/customer/renew/begin'
    }
  }, []);


  return (
    <RenewalContext.Provider
      value={{
        renewalData,
        updateRenewalData,
        saveRenewalData,
        saveRenewalDataForLater,
        getRenewalData
      }}
    >
      {children}
    </RenewalContext.Provider>
  )
}

export function useRenewal() {
  const context = useContext(RenewalContext)

  if(!context) throw new Error('useRenewal must be used within an RenewalProvider!')

  return context
}